import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Button, Grid, Typography} from "@mui/material";
import PartnerAddFundingSourceDialog from "../PartnerAddFundingSourceDialog";
import PartnerFundingSourcesList from "../PartnerFundingSourcesList";
import RemovePartnerFundingSourceDialog from "../RemovePartnerFundingSourceDialog";
import {withSnackbar} from "../hocs/withSnackbar";
import {requestLoggedInAccount} from "../../services/session/session-requests";
import PartnerVerifyBankAccountDialog from "./PartnerVerifyBankAccountDialog";
import {requestGetFundingSourceId} from "./request/payment-method-requests";
import EmailVerifyToolTip from "../notifications/EmailVerifyToolTip";
import {styled} from "@mui/material/styles";

const Gap15 = styled(Grid)(() => ({
  gap: '1.07rem'
}));

const AddPaymentButton = styled(Button)(() => ({
  textTransform: 'uppercase'
}));

const PartnerPaymentMethods = (props) => {

  const {account, snackbarShowMessage} = props;
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showDeletePaymentDialog, setShowDeletePaymentDialog] = useState(false);
  const [showVerifyPaymentDialog, setShowVerifyPaymentDialog] = useState(false);
  const [fundingDetails, setFundingDetails] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const detailsReady = fundingDetails?.account.id === account.id;
  const fundingSourceExists = detailsReady && !!fundingDetails.id;

  useEffect(() => {
    let active = true;
    setFundingDetails(null);
    setLoadFailed(false);
    Promise.all([requestLoggedInAccount(), requestGetFundingSourceId(account.id)])
      .then(([details, source]) => {
        if (!active) return;
        // A session switch or a failed Dwolla enrichment must not look like an empty account.
        if (details.body.id !== account.id || (source.text &&
            (!details.body.existingFundingSourceBankName || !details.body.existingFundingSourceBankAccountType))) {
          throw new Error('Funding source details unavailable');
        }
        setFundingDetails({account: details.body, id: source.text});
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => { active = false; };
  }, [account.id, refreshVersion]);

  const openAddPaymentModal = () => {
    setShowAddPaymentModal(true);
  };

  const refreshFundingDetails = () => {
    setFundingDetails(null);
    setLoadFailed(false);
    setRefreshVersion(version => version + 1);
  };

  const handleAddFundingSourceSuccess = () => {
    refreshFundingDetails();
    setShowAddPaymentModal(false);
  };

  const handleVerifyPaymentSuccess = () => {
    snackbarShowMessage('Bank Account verified successfully', 'success', 8000);
    refreshFundingDetails();
    setShowVerifyPaymentDialog(false);
  }

  const handleCloseRemovePaymentDialog = () => {
    setShowDeletePaymentDialog(false);
  };

  const handlePaymentRemove = () => {
    refreshFundingDetails();
    setShowDeletePaymentDialog(false);
    snackbarShowMessage('Funding Source removed successfully', 'success', 8000);
  };

  const handlePaymentMenuAction = useCallback((actionName) => {
    switch (actionName) {
      case 'remove':
        setShowDeletePaymentDialog(true);
        break;
      case 'verify':
        setShowVerifyPaymentDialog(true);
        break;
      default:
        break;
    }
  }, [setShowDeletePaymentDialog]);

  return (
    <Gap15 container direction={'column'}>
      <Gap15 item container justifyContent={'space-between'}>
        <Typography variant={'h5'} component={'h1'}>Payout Bank Account</Typography>
        <EmailVerifyToolTip
          title={"Please verify your email before you can add a bank account."}
          isEmailVerified={account?.emailVerified}
        >
          <AddPaymentButton
            onClick={openAddPaymentModal}
            color='secondary'
            variant='contained'
            disabled={!account?.emailVerified || !detailsReady}
          >
            {`${fundingSourceExists ? "Change" : "Add"} Bank Account`}
          </AddPaymentButton>
        </EmailVerifyToolTip>
      </Gap15>
      {loadFailed ? <Alert severity="error" action={<Button onClick={refreshFundingDetails}>Retry</Button>}>
        Unable to load your bank account. Please try again.
      </Alert> : !detailsReady ? <Typography role="status">Loading bank account…</Typography> :
        <PartnerFundingSourcesList
          account={fundingDetails.account}
          onMenuAction={handlePaymentMenuAction}
          fundingSourceExists={fundingSourceExists}
        />
      }
      <PartnerVerifyBankAccountDialog
        fundingSourceId={detailsReady ? fundingDetails.id : ''}
        accountId={account?.id}
        open={showVerifyPaymentDialog}
        onClose={() => setShowVerifyPaymentDialog(false)}
        onSuccess={handleVerifyPaymentSuccess}
      />
      <PartnerAddFundingSourceDialog
        account={account}
        open={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onSuccess={handleAddFundingSourceSuccess}
      />
      <RemovePartnerFundingSourceDialog
        open={showDeletePaymentDialog}
        onClose={handleCloseRemovePaymentDialog}
        account={account}
        onSuccess={handlePaymentRemove}
      />
    </Gap15>
  );
};

export default withSnackbar(PartnerPaymentMethods);
